// SubscriptionPlans Service (5.3 Subscription/Order Module)
// Manages CRUD operations for subscription plans

import { BaseService } from '@/chassis/data/services/base-service';
import { ValidationRules } from '@/chassis/data/services/base-form-service';
import { createNormalizer, FieldTransforms } from '@/chassis/data/services/normalizer-utils';
import { createSimpleFormService } from '@/chassis/data/services/simple-form-service';

export interface SubscriptionPlanRecord {
    id: string;
    planName: string;
    planDescription?: string;
    planType: string;
    planCategory?: string;
    planTier: string;
    active: boolean;
    status?: string; // Status do plano: draft, active, inactive, archived
    isPublic?: boolean;
    billingCycle: string;
    /** Abrangencia: NATIONAL (todas as licitacoes) ou AGENCY (so as de um orgao). */
    scope?: 'NATIONAL' | 'AGENCY';
    agencyId?: string;
    agencyName?: string;
    pricing?: string;
    features?: string;
    limitations?: string;
    quotas?: string;
    addOns?: string;
    discounts?: string;
    promotions?: string;
    metadata?: string;
    tags?: string;
    sortOrder?: number;
    effectiveDate: string;
    expirationDate?: string;
    gatewayPlanId?: string; // ID do plano no gateway de pagamento (ex: Mercado Pago)
    createdAt?: string;
    updatedAt?: string;
    createdBy?: string;
    createdByName?: string;
    createdByUser?: string;
    updatedBy?: string;
    updatedByName?: string;
    updatedByUser?: string;
}

export interface SubscriptionPlanFilters {
    searchTerm?: string;
    planType?: string;
    planTier?: string;
    billingCycle?: string;
    visibility?: string;
    active?: string;
}

export interface SubscriptionPlanFormData extends Partial<SubscriptionPlanRecord> {
    planName: string;
    planType: string;
    planTier: string;
    billingCycle: string;
    effectiveDate: string;
    active: boolean;
}

const stringifyIfObject = (value: unknown): string | undefined => {
    if (value === undefined || value === null) return undefined;
    if (typeof value === 'string') return value;
    try {
        return JSON.stringify(value, null, 2);
    } catch {
        return String(value);
    }
};

export const normalizeSubscriptionPlan = createNormalizer<SubscriptionPlanRecord>({
    fields: [
        'planName',
        'planDescription',
        'planType',
        'planCategory',
        'planTier',
        { target: 'active', transform: FieldTransforms.toBoolean },
        'status', // Status do plano (draft/active/inactive/archived)
        'isPublic',
        'billingCycle',
        'agencyName',
        'pricing',
        'limitations',
        'quotas',
        'tags',
        { target: 'sortOrder', transform: FieldTransforms.toNumber },
        'effectiveDate',
        'expirationDate',
        'gatewayPlanId', // ID do plano no gateway de pagamento
        { target: 'createdAt', transform: FieldTransforms.identity },
        { target: 'updatedAt', transform: FieldTransforms.identity },
        { target: 'createdBy', transform: FieldTransforms.identity },
        { target: 'createdByName', transform: FieldTransforms.identity },
        { target: 'createdByUser', transform: FieldTransforms.identity },
        { target: 'updatedBy', transform: FieldTransforms.identity },
        { target: 'updatedByName', transform: FieldTransforms.identity },
        { target: 'updatedByUser', transform: FieldTransforms.identity },
    ],
    customize: (record, raw) => {
        const resolvedActive = raw?.active ?? record.active;
        record.active = resolvedActive === undefined ? true : Boolean(resolvedActive);
        record.isPublic = raw?.isPublic === true;
        record.expirationDate = raw?.expirationDate ?? record.expirationDate;
        // Planos sem abrangencia sao os nacionais de sempre.
        const agencyId = String(raw?.agencyId ?? '').split(':').pop() ?? '';
        record.scope = String(raw?.scope ?? '').toUpperCase() === 'AGENCY' && agencyId ? 'AGENCY' : 'NATIONAL';
        record.agencyId = record.scope === 'AGENCY' ? agencyId : undefined;

        // Sincronizar status com active se status não estiver presente
        if (!raw?.status && raw?.active !== undefined) {
            record.status = raw.active ? 'active' : 'inactive';
        }

        record.features = stringifyIfObject(raw?.features);
        record.addOns = stringifyIfObject(raw?.addOns);
        record.discounts = stringifyIfObject(raw?.discounts);
        record.promotions = stringifyIfObject(raw?.promotions);
        record.metadata = stringifyIfObject(raw?.metadata);
    },
});

class SubscriptionPlansService extends BaseService<SubscriptionPlanRecord, SubscriptionPlanFormData, Partial<SubscriptionPlanFormData>, SubscriptionPlanFilters> {
    constructor() {
        super({
            basePath: '/subscriptionplans',
            normalizeRecord: normalizeSubscriptionPlan,
        });
    }

    /**
     * @description Alterna o status ativo/inativo de um plano
     * Sincroniza automaticamente os campos 'active' (local) e 'status' (gateway)
     */
    async toggleActive(id: string | number, active: boolean): Promise<SubscriptionPlanRecord> {
        console.log('[SubscriptionPlans] toggleActive:', { id, active });
        // Envia tanto 'active' (local) quanto 'status' (gateway)
        const payload: Partial<SubscriptionPlanFormData> = {
            active,
            status: active ? 'active' : 'inactive',
        } as any; // Cast necessário porque 'status' não está em SubscriptionPlanFormData
        const result = await this.update(id, payload);
        console.log('[SubscriptionPlans] toggleActive result:', result);
        return result;
    }
}

export const subscriptionPlansService = new SubscriptionPlansService();

// Simple form service - no complex transformations needed
export const createSubscriptionPlansFormService = () =>
    createSimpleFormService<SubscriptionPlanRecord, SubscriptionPlanFormData>({
        basePath: '/subscriptionplans',
        initialData: {
            planName: '',
            planType: '',
            planTier: '',
            billingCycle: '',
            scope: 'NATIONAL',
            effectiveDate: '',
            active: true,
            status: 'active',
            planDescription: '',
            planCategory: '',
            pricing: '',
            limitations: '',
            quotas: '',
            addOns: '',
            features: '',
            discounts: '',
            promotions: '',
            metadata: '',
            tags: '',
            sortOrder: undefined,
            expirationDate: '',
            isPublic: false,
            gatewayPlanId: '',
        },
        validationRules: {
            planName: [ValidationRules.required('Nome do plano é obrigatório')],
            planType: [ValidationRules.required('Tipo de plano é obrigatório')],
            planTier: [ValidationRules.required('Nível do plano é obrigatório')],
            billingCycle: [ValidationRules.required('Ciclo de cobrança é obrigatório')],
            effectiveDate: [ValidationRules.required('Data de início é obrigatória')],
            agencyId: [
                {
                    validate: (value: unknown, data?: Partial<SubscriptionPlanFormData>) => data?.scope !== 'AGENCY' || !!String(value ?? '').trim(),
                    message: 'Escolha o órgão do plano',
                } as any,
            ],
        },
        normalizeRecord: normalizeSubscriptionPlan,
    });
